import { STORED_DEPTH_IN } from "./soilUnits";
import { METRES_PER_DEGREE_LAT, METRES_PER_DEGREE_LNG } from "./metres";

// Per-cell PLUMBING for the at-depth display: which property a layer is, which CELL a point falls
// in, and how a value is labelled.

export interface SoilBlob {
  cec: number;
  om?: number;
  ph?: number;
  p?: number;
  k?: number;
  ca?: number;
  mg?: number;
}

/* * Nothing to do at the stored depth — every solve is an identity there, so skip the work entirely. */
export function needsDepthConversion(depthIn: number): boolean {
  return Number.isFinite(depthIn) && depthIn !== STORED_DEPTH_IN;
}

/*
 *
 * Which BLOB PROPERTY a layer's value is, as opposed to which extractant choice governs it.
 *
 * `analyteOf` answers the second question and collapses K/Ca/Mg into one `'k'`, because one picker choice
 * governs the cation suite. Using that as the property key would read a Mg layer's converted value out of
 * the `k` slot — a different number entirely. These are two questions and they need two functions.
 */
function soilToken(keyOrTitle: string | null | undefined): string {
  return (keyOrTitle ?? "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/* * Buffer pH is its own reading. It must not be solved as water pH or as phosphorus. */
export function isBufferPhKey(keyOrTitle: string | null | undefined): boolean {
  const s = soilToken(keyOrTitle);
  return /\bbuffer ph\b|\bbph\b/.test(s);
}

export function blobPropOf(
  keyOrTitle: string | null | undefined,
): keyof SoilBlob | null {
  const s = soilToken(keyOrTitle);
  if (!s) return null;
  if (isBufferPhKey(s)) return null;
  if (/\bph\b/.test(s)) return "ph";
  if (/\bom\b|organic matter/.test(s)) return "om";
  if (/\bcec\b/.test(s)) return "cec";
  if (/\bp\b|phosph/.test(s)) return "p";
  if (/\bk\b|potass/.test(s)) return "k";
  const tokens = s.split(" ").map((t) => t.replace(/[()]/g, ""));
  if (tokens.includes("ca") || tokens.includes("calcium")) return "ca";
  if (tokens.includes("mg") || tokens.includes("magnesium")) return "mg";
  return null;
}

/* * ~8 m, one grid cell. Latitude and longitude are different lengths. */
const CELL_SNAP_LAT = 8 / METRES_PER_DEGREE_LAT;
const CELL_SNAP_LNG = 8 / METRES_PER_DEGREE_LNG;

export function snapToCell(
  lng: number,
  lat: number,
): { lng: number; lat: number } {
  return {
    lng: Math.round(lng / CELL_SNAP_LNG) * CELL_SNAP_LNG,
    lat: Math.round(lat / CELL_SNAP_LAT) * CELL_SNAP_LAT,
  };
}

/*
 *
 * Cache key for one converted cell. Keyed on the DEPTH as well as the cell, because the same cell at two
 * depths is two different answers — and on the field, because cell indices are per-field.
 */
export function depthCacheKey(
  fieldId: string,
  depthIn: number,
  lng: number,
  lat: number,
): string {
  // Snap FIRST. The key is per-CELL, not per-coordinate — see `snapToCell`.
  const s = snapToCell(lng, lat);
  return `${fieldId}|${depthIn}|${s.lng.toFixed(6)},${s.lat.toFixed(6)}`;
}

/* * Suffix for a value that has been re-read at another depth. Absent at the stored depth. */
export function depthSuffix(depthIn: number, converted: boolean): string {
  if (!needsDepthConversion(depthIn)) return "";
  // An UNconverted number at a non-default depth must say it is still 8″, or the panel's promise that
  // values were re-read would silently cover a value that wasn't.
  return converted ? ` @ ${depthIn}″` : ` @ ${STORED_DEPTH_IN}″`;
}
